// src/pages/admin/ActivationReport.jsx
import React, { useState, useEffect } from 'react';
import { database, ref, onValue } from '../../firebaseConfig';
import { 
  Download, 
  Filter, 
  Search, 
  ChevronDown, 
  ChevronUp, 
  CheckCircle, 
  XCircle, 
  Gift,
  Users,
  DollarSign,
  Package,
  Calendar,
  TrendingUp,
  ArrowUpDown,
  Eye,
  Copy,
  Clock,
  CreditCard,
  Receipt,
  User,
  Sparkles
} from 'lucide-react';

const ActivationReport = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedMobileTx, setExpandedMobileTx] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showTransactionDialog, setShowTransactionDialog] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    expired: 0,
    totalAmount: 0,
    activeAmount: 0,
    expiredAmount: 0
  });

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    filterAndSortTransactions();
  }, [transactions, searchTerm, statusFilter, typeFilter, dateFilter, sortBy, sortOrder]);

  const fetchTransactions = async () => {
    try {
      const usersRef = ref(database, 'users');
      
      onValue(usersRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          let transactionsArray = [];
          let totalActive = 0;
          let totalExpired = 0;
          let totalActiveAmount = 0;
          let totalExpiredAmount = 0;
          
          // Iterate through each date (e.g., 2026-02-06)
          Object.keys(data).forEach(dateKey => {
            const dateData = data[dateKey];
            
            // Check if dateData is an object (not null or undefined)
            if (dateData && typeof dateData === 'object') {
              // Iterate through users under this date
              Object.keys(dateData).forEach(userKey => {
                const user = dateData[userKey];
                
                // Skip admin users or invalid entries
                if (!user || userKey === 'admin' || user.userId === 'admin' || user.role === 'admin') {
                  return;
                }
                
                // Process user's packages
                if (user.packages && Array.isArray(user.packages)) {
                  user.packages.forEach((pkg, index) => {
                    // Determine package type
                    let packageType = 'purchase';
                    if (pkg.directUplineId || pkg.giftedBy) {
                      packageType = 'gift';
                    }
                    
                    // Generate member ID
                    const memberId = user.memberId || user.userId || `AV${String(userKey).substring(0, 6)}`;
                    
                    // Generate transaction ID
                    const transactionId = `ACT${userKey.substring(0, 4)}${dateKey.replace(/-/g, '').substring(2)}${index}`;
                    
                    // Get package amount
                    const baseAmount = parseFloat(pkg.baseAmount) || parseFloat(pkg.investmentAmount) || 0;
                    
                    // Determine status
                    let status = 'active';
                    if (pkg.status === 'expired' || pkg.status === 'inactive') {
                      status = 'expired';
                    }
                    
                    // Count stats
                    if (status === 'active') {
                      totalActive++;
                      totalActiveAmount += baseAmount;
                    } else {
                      totalExpired++;
                      totalExpiredAmount += baseAmount;
                    }
                    
                    // Create transaction object
                    const transaction = {
                      id: `${dateKey}_${userKey}_${index}`,
                      firebaseDateKey: dateKey,
                      firebaseUserKey: userKey,
                      packageIndex: index,
                      transactionId: transactionId,
                      transactionType: packageType,
                      type: packageType === 'gift' ? 'package_gift' : 'package_purchase',
                      userName: user.name || user.fullName || 'Unknown User',
                      userId: user.userId || userKey,
                      memberId: memberId,
                      targetUserName: pkg.giftedToName || pkg.receiverName || null,
                      targetUserId: pkg.giftedToId || pkg.receiverId || null,
                      packageName: pkg.packageName || pkg.packageId || 'Package',
                      packageId: pkg.packageId || pkg.packagePlan || 'PKG',
                      baseAmount: baseAmount,
                      amount: baseAmount,
                      totalAmount: baseAmount,
                      date: pkg.activationDate || pkg.purchaseDate || user.registrationDate || dateKey,
                      activationDate: pkg.activationDate || pkg.purchaseDate || null,
                      status: status,
                      packageStatus: pkg.status || 'active',
                      durationDays: pkg.durationDays || 0,
                      dailyROI: pkg.dailyROI || 0,
                      dailyEnergyAmount: pkg.dailyEnergyAmount || 0,
                      directUplineId: pkg.directUplineId || pkg.sponsorId || null,
                      giftedBy: pkg.giftedBy || null,
                      paymentMethod: pkg.paymentMethod || pkg.walletUsed || 'wallet',
                      transactionDetails: pkg.transactionDetails || null
                    };
                    
                    transactionsArray.push(transaction);
                  });
                } else if (user.totalInvestment && user.totalInvestment > 0) {
                  // If user has totalInvestment but no packages array, create a transaction from totalInvestment
                  const memberId = user.memberId || user.userId || `AV${String(userKey).substring(0, 6)}`;
                  const transactionId = `ACT${userKey.substring(0, 4)}${dateKey.replace(/-/g, '').substring(2)}0`;
                  
                  const baseAmount = parseFloat(user.totalInvestment) || 0;
                  
                  if (baseAmount > 0) {
                    totalActive++;
                    totalActiveAmount += baseAmount;
                    
                    const transaction = {
                      id: `${dateKey}_${userKey}_0`,
                      firebaseDateKey: dateKey,
                      firebaseUserKey: userKey,
                      packageIndex: 0,
                      transactionId: transactionId,
                      transactionType: 'purchase',
                      type: 'package_purchase',
                      userName: user.name || user.fullName || 'Unknown User',
                      userId: user.userId || userKey,
                      memberId: memberId,
                      packageName: 'Investment Package',
                      packageId: 'INVEST',
                      baseAmount: baseAmount,
                      amount: baseAmount,
                      totalAmount: baseAmount,
                      date: user.registrationDate || dateKey,
                      activationDate: user.registrationDate || dateKey,
                      status: 'active',
                      packageStatus: 'active',
                      paymentMethod: 'wallet'
                    };
                    
                    transactionsArray.push(transaction);
                  }
                }
              });
            }
          });
          
          console.log(`Found ${transactionsArray.length} package activations`);
          console.log(`Active: ${totalActive}, Expired: ${totalExpired}`);
          console.log(`Active Amount: $${totalActiveAmount}, Expired Amount: $${totalExpiredAmount}`);
          
          setTransactions(transactionsArray);
          setStats({
            total: transactionsArray.length,
            active: totalActive,
            expired: totalExpired,
            totalAmount: totalActiveAmount + totalExpiredAmount,
            activeAmount: totalActiveAmount,
            expiredAmount: totalExpiredAmount
          });
        } else {
          console.log("No users found in database");
          setTransactions([]);
          setStats({
            total: 0,
            active: 0,
            expired: 0,
            totalAmount: 0,
            activeAmount: 0,
            expiredAmount: 0
          });
        }
        setLoading(false);
      }, (error) => {
        console.error('Error fetching users:', error);
        setLoading(false);
      });
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const filterAndSortTransactions = () => {
    let filtered = [...transactions];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(t => 
        t.userName?.toLowerCase().includes(term) ||
        t.targetUserName?.toLowerCase().includes(term) ||
        t.packageName?.toLowerCase().includes(term) ||
        t.packageId?.toLowerCase().includes(term) ||
        t.transactionId?.toLowerCase().includes(term) ||
        t.userId?.toLowerCase().includes(term) ||
        t.targetUserId?.toLowerCase().includes(term) ||
        t.memberId?.toLowerCase().includes(term) ||
        t.id?.toLowerCase().includes(term)
      );
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(t => t.type === typeFilter || t.transactionType === typeFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(t => t.status === statusFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch(dateFilter) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          filtered = filtered.filter(t => {
            const tDate = new Date(t.date);
            return tDate >= filterDate;
          });
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          filtered = filtered.filter(t => {
            const tDate = new Date(t.date);
            return tDate >= filterDate;
          });
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          filtered = filtered.filter(t => {
            const tDate = new Date(t.date);
            return tDate >= filterDate;
          });
          break;
      }
    }

    // Sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch(sortBy) {
        case 'date':
          aValue = new Date(a.date).getTime();
          bValue = new Date(b.date).getTime();
          break;
        case 'amount':
          aValue = a.amount || 0;
          bValue = b.amount || 0;
          break;
        case 'name':
          aValue = a.userName?.toLowerCase() || '';
          bValue = b.userName?.toLowerCase() || '';
          break;
        case 'package':
          aValue = a.packageName?.toLowerCase() || '';
          bValue = b.packageName?.toLowerCase() || '';
          break;
        default:
          aValue = new Date(a.date).getTime();
          bValue = new Date(b.date).getTime();
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredTransactions(filtered);
  };

  const toggleRowExpansion = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const toggleMobileTx = (id) => {
    setExpandedMobileTx(expandedMobileTx === id ? null : id);
  };

  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionDialog(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const TransactionDialog = () => {
    if (!selectedTransaction) return null;

    const t = selectedTransaction;
    const isGift = t.type === 'package_gift' || t.transactionType === 'gift';

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-secondary border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="p-4 border-b border-border flex justify-between items-center sticky top-0 bg-secondary z-10">
            <h3 className="text-lg font-bold text-foreground font-heading flex items-center">
              <Package className="h-5 w-5 mr-2 text-primary" />
              Package Activation Details
            </h3>
            <button
              onClick={() => setShowTransactionDialog(false)}
              className="text-muted-foreground hover:text-foreground text-xl"
            >
              ✕
            </button>
          </div>
          
          <div className="p-4">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 p-4 bg-secondary/50 rounded-lg">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                    isGift 
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' 
                      : 'bg-green-500/20 text-green-400 border border-green-500/30'
                  }`}>
                    {isGift ? (
                      <>
                        <Gift size={14} />
                        Gifted Package
                      </>
                    ) : (
                      <>
                        <CheckCircle size={14} />
                        Self Purchase
                      </>
                    )}
                  </span>
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                    t.status === 'active' 
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                      : 'bg-red-500/20 text-red-400 border border-red-500/30'
                  }`}>
                    {t.status === 'active' ? 'Active' : 'Expired'}
                  </span>
                </div>
                <div className="text-foreground font-heading text-xl mb-1">{t.packageName}</div>
                <div className="text-muted-foreground text-sm font-mono flex items-center">
                  <Receipt className="h-3 w-3 mr-1" />
                  Txn ID: {t.transactionId}
                </div>
              </div>
              <div className="mt-4 md:mt-0">
                <div className="text-3xl font-bold text-green-400 font-heading">
                  ${t.amount?.toFixed(2)}
                </div>
                <div className="text-muted-foreground text-sm text-right">
                  {formatDate(t.activationDate || t.date)}
                </div>
              </div>
            </div>

            {/* Grid Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                {/* Buyer Information */}
                <div className="bg-secondary/30 border border-border rounded-lg p-4">
                  <h4 className="text-muted-foreground font-semibold text-sm mb-3 flex items-center font-heading">
                    <User className="h-4 w-4 mr-2" />
                    {isGift ? 'Gift Giver' : 'Buyer Information'}
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground font-body">Name:</span>
                      <span className="text-sm text-foreground font-body font-medium">{t.userName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground font-body">User ID:</span>
                      <div className="flex items-center">
                        <span className="text-sm text-foreground font-body font-mono mr-2">{t.userId}</span>
                        <button
                          onClick={() => copyToClipboard(t.userId)}
                          className="text-primary hover:text-primary/80"
                          title="Copy User ID"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground font-body">Member ID:</span>
                      <span className="text-sm text-foreground font-body font-mono">{t.memberId}</span>
                    </div>
                  </div>
                </div>

                {/* Package Details */}
                <div className="bg-secondary/30 border border-border rounded-lg p-4">
                  <h4 className="text-muted-foreground font-semibold text-sm mb-3 flex items-center font-heading">
                    <Package className="h-4 w-4 mr-2" />
                    Package Details
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground font-body">Package Name:</span>
                      <span className="text-sm text-foreground font-body">{t.packageName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground font-body">Package ID:</span>
                      <span className="text-sm text-foreground font-body font-mono">{t.packageId}</span>
                    </div>
                    {t.durationDays && (
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground font-body">Duration:</span>
                        <span className="text-sm text-foreground font-body">{t.durationDays} days</span>
                      </div>
                    )}
                    {t.dailyROI && (
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground font-body">Daily ROI:</span>
                        <span className="text-sm text-green-400 font-body">{t.dailyROI}%</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                {/* Amount Breakdown */}
                <div className="bg-secondary/30 border border-border rounded-lg p-4">
                  <h4 className="text-muted-foreground font-semibold text-sm mb-3 flex items-center font-heading">
                    <DollarSign className="h-4 w-4 mr-2" />
                    Investment Details
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground font-body">Base Amount:</span>
                      <span className="text-lg text-foreground font-heading">${t.baseAmount?.toFixed(2)}</span>
                    </div>
                    {t.dailyEnergyAmount && t.dailyEnergyAmount > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground font-body">Daily Earning:</span>
                        <span className="text-lg text-green-400 font-heading">${t.dailyEnergyAmount.toFixed(4)}</span>
                      </div>
                    )}
                    <div className="pt-3 border-t border-border">
                      <div className="flex justify-between items-center">
                        <span className="text-base text-muted-foreground font-heading">Total Investment:</span>
                        <span className="text-2xl font-bold text-green-400 font-heading">${t.amount?.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Receiver Information (for gifts) */}
                {isGift && t.targetUserName && (
                  <div className="bg-secondary/30 border border-border rounded-lg p-4">
                    <h4 className="text-muted-foreground font-semibold text-sm mb-3 flex items-center font-heading">
                      <Gift className="h-4 w-4 mr-2" />
                      Gift Receiver
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground font-body">Receiver Name:</span>
                        <span className="text-sm text-foreground font-body font-medium">{t.targetUserName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground font-body">Receiver ID:</span>
                        <div className="flex items-center">
                          <span className="text-sm text-foreground font-body font-mono mr-2">{t.targetUserId}</span>
                          <button
                            onClick={() => copyToClipboard(t.targetUserId)}
                            className="text-primary hover:text-primary/80"
                            title="Copy Receiver ID"
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Information */}
            <div className="mt-6 bg-secondary/30 border border-border rounded-lg p-4">
              <h4 className="text-muted-foreground font-semibold text-sm mb-3 flex items-center font-heading">
                <Clock className="h-4 w-4 mr-2" />
                Additional Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground font-body">Activation Date:</span>
                    <span className="text-sm text-foreground font-body">{formatDate(t.activationDate || t.date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground font-body">Payment Method:</span>
                    <span className="text-sm text-yellow-400 font-body">{t.paymentMethod || 'Wallet'}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground font-body">Transaction ID:</span>
                    <div className="flex items-center">
                      <span className="text-sm text-foreground font-body font-mono mr-2">{t.transactionId}</span>
                      <button
                        onClick={() => copyToClipboard(t.transactionId)}
                        className="text-primary hover:text-primary/80"
                        title="Copy Transaction ID"
                      >
                        <Copy className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground font-body">Status:</span>
                    <span className={`text-sm font-medium ${
                      t.status === 'active' ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {t.status === 'active' ? 'Active' : 'Expired'}
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground font-body">Transaction Type:</span>
                    <span className={`text-sm font-medium ${
                      isGift ? 'text-blue-400' : 'text-green-400'
                    }`}>
                      {isGift ? 'Gift Package' : 'Self Purchase'}
                    </span>
                  </div>
                  {t.directUplineId && (
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground font-body">Upline ID:</span>
                      <span className="text-sm text-purple-400 font-body font-mono">{t.directUplineId}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="p-4 border-t border-border flex gap-3">
            <button
              onClick={() => {
                copyToClipboard(
                  `Package Activation Details\n` +
                  `Transaction ID: ${t.transactionId}\n` +
                  `Buyer: ${t.userName} (ID: ${t.userId})\n` +
                  `Member ID: ${t.memberId}\n` +
                  `Package: ${t.packageName} (${t.packageId})\n` +
                  `Amount: $${t.amount?.toFixed(2)}\n` +
                  `Type: ${isGift ? 'Gift Package' : 'Self Purchase'}\n` +
                  `Status: ${t.status === 'active' ? 'Active' : 'Expired'}\n` +
                  `Activation Date: ${formatDate(t.activationDate || t.date)}` +
                  (isGift && t.targetUserName ? `\nReceiver: ${t.targetUserName} (ID: ${t.targetUserId})` : '') +
                  (t.directUplineId ? `\nUpline ID: ${t.directUplineId}` : '')
                );
              }}
              className="flex-1 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors font-body text-sm flex items-center justify-center"
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy Details
            </button>
            <button
              onClick={() => setShowTransactionDialog(false)}
              className="flex-1 px-4 py-2 bg-secondary hover:bg-secondary/80 text-foreground rounded-lg transition-colors font-body text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const exportToCSV = () => {
    const headers = ['Transaction ID', 'Type', 'Buyer', 'Buyer ID', 'Member ID', 'Receiver', 'Receiver ID', 'Package', 'Package ID', 'Amount', 'Activation Date', 'Status', 'Daily ROI', 'Duration'];
    
    const csvData = filteredTransactions.map(t => [
      t.transactionId,
      t.type === 'package_gift' ? 'Gifted' : 'Self Purchase',
      t.userName,
      t.userId,
      t.memberId,
      t.targetUserName || 'N/A',
      t.targetUserId || 'N/A',
      t.packageName,
      t.packageId,
      `$${t.amount?.toFixed(2) || '0.00'}`,
      formatDate(t.activationDate || t.date),
      t.status === 'active' ? 'Active' : 'Expired',
      `${t.dailyROI || 0}%`,
      `${t.durationDays || 0} days`
    ]);
    
    const csvContent = [headers, ...csvData]
      .map(row => row.join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `package-activations-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const formatCompactDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  // Desktop Table Row Component
  const DesktopTableRow = ({ transaction }) => {
    const isGift = transaction.type === 'package_gift' || transaction.transactionType === 'gift';
    
    return (
      <React.Fragment key={transaction.id}>
        <tr className="border-b border-gray-800/50 hover:bg-secondary/10 transition-colors text-xs">
          {/* Transaction ID */}
          <td className="py-2 px-1">
            <div className="text-blue-400 font-mono truncate max-w-[100px]" title={transaction.transactionId}>
              {transaction.transactionId}
            </div>
          </td>

          {/* Type */}
          <td className="py-2 px-1">
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium ${
              isGift
                ? 'bg-blue-500/20 text-blue-400' 
                : 'bg-green-500/20 text-green-400'
            }`}>
              {isGift ? (
                <>
                  <Gift size={10} />
                  Gift
                </>
              ) : (
                <>
                  <User size={10} />
                  Self
                </>
              )}
            </span>
          </td>

          {/* Buyer */}
          <td className="py-2 px-1">
            <div className="truncate max-w-[90px]" title={transaction.userName}>
              <div className="text-foreground text-[11px]">{transaction.userName}</div>
              <div className="text-muted-foreground text-[10px]">{transaction.memberId}</div>
            </div>
          </td>

          {/* Package */}
          <td className="py-2 px-1">
            <div className="text-foreground text-[11px] truncate max-w-[80px]" title={transaction.packageName}>
              {transaction.packageName}
            </div>
          </td>

          {/* Amount */}
          <td className="py-2 px-1 text-right">
            <div className="text-green-400 font-bold text-sm">
              ${transaction.amount?.toFixed(0) || '0'}
            </div>
          </td>

          {/* Activation Date */}
          <td className="py-2 px-1">
            <div className="text-muted-foreground text-[11px]">
              {formatDate(transaction.activationDate || transaction.date)}
            </div>
          </td>

          {/* Status */}
          <td className="py-2 px-1">
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium ${
              transaction.status === 'active' 
                ? 'bg-green-500/20 text-green-400' 
                : 'bg-red-500/20 text-red-400'
            }`}>
              {transaction.status === 'active' && <CheckCircle size={10} />}
              {transaction.status === 'expired' && <XCircle size={10} />}
              {transaction.status === 'active' ? 'Active' : 'Expired'}
            </span>
          </td>

          {/* Actions */}
          <td className="py-2 px-1">
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleViewTransaction(transaction)}
                className="p-1 hover:bg-secondary/50 rounded transition-colors"
                title="View Details"
              >
                <Eye className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
              <button
                onClick={() => toggleRowExpansion(transaction.id)}
                className="p-1 hover:bg-secondary/50 rounded transition-colors"
                title="Expand Details"
              >
                {expandedRows[transaction.id] ? (
                  <ChevronUp size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </button>
            </div>
          </td>
        </tr>
        
        {/* Expanded Details Row */}
        {expandedRows[transaction.id] && (
          <tr className="bg-secondary/10 border-b border-gray-800">
            <td colSpan="8" className="py-3 px-3">
              <div className="bg-secondary/20 rounded-lg p-3 card-glow">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <h4 className="text-muted-foreground text-xs mb-2 flex items-center font-heading">
                      <Package className="h-3 w-3 mr-1" />
                      Package Details
                    </h4>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Package ID:</span>
                        <span className="text-foreground font-mono">{transaction.packageId}</span>
                      </div>
                      {transaction.dailyROI && (
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Daily ROI:</span>
                          <span className="text-green-400">{transaction.dailyROI}%</span>
                        </div>
                      )}
                      {transaction.durationDays && (
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Duration:</span>
                          <span className="text-foreground">{transaction.durationDays} days</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-muted-foreground text-xs mb-2 flex items-center font-heading">
                      <Users className="h-3 w-3 mr-1" />
                      {isGift ? 'Receiver Info' : 'Buyer Info'}
                    </h4>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">User ID:</span>
                        <span className="text-foreground font-mono">{transaction.userId}</span>
                      </div>
                      {isGift && transaction.targetUserName && (
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Receiver:</span>
                          <span className="text-foreground">{transaction.targetUserName}</span>
                        </div>
                      )}
                      {transaction.directUplineId && (
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Upline ID:</span>
                          <span className="text-purple-400 font-mono">{transaction.directUplineId}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-muted-foreground text-xs mb-2 flex items-center font-heading">
                      <DollarSign className="h-3 w-3 mr-1" />
                      Investment Info
                    </h4>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Base Amount:</span>
                        <span className="text-foreground">${transaction.baseAmount?.toFixed(2)}</span>
                      </div>
                      {transaction.dailyEnergyAmount && transaction.dailyEnergyAmount > 0 && (
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Daily Earning:</span>
                          <span className="text-green-400">${transaction.dailyEnergyAmount.toFixed(4)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Payment:</span>
                        <span className="text-yellow-400">{transaction.paymentMethod || 'Wallet'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  // Mobile Accordion Item Component
  const MobileAccordionItem = ({ transaction, index }) => {
    const isGift = transaction.type === 'package_gift' || transaction.transactionType === 'gift';
    
    return (
      <div key={transaction.id} className="card-glow mb-4 overflow-hidden">
        <div 
          className="p-4 bg-secondary hover:bg-secondary/80 transition-colors cursor-pointer flex justify-between items-center"
          onClick={() => toggleMobileTx(transaction.id)}
        >
          <div className="flex items-center gap-3">
            <div className="text-silver font-body text-sm font-mono">#{index + 1}</div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  isGift
                    ? 'bg-blue-500/20 text-blue-400' 
                    : 'bg-green-500/20 text-green-400'
                }`}>
                  {isGift ? (
                    <>
                      <Gift size={10} />
                      Gift
                    </>
                  ) : (
                    <>
                      <User size={10} />
                      Self
                    </>
                  )}
                </span>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  transaction.status === 'active' 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-red-500/20 text-red-400'
                }`}>
                  {transaction.status === 'active' ? 'Active' : 'Expired'}
                </span>
              </div>
              <div className="text-xs text-muted-foreground font-body">
                {transaction.userName} • {transaction.packageName}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <div className="text-green-400 font-bold font-heading text-sm">
                ${transaction.amount?.toFixed(0)}
              </div>
              <div className="text-xs text-muted-foreground">
                {formatCompactDate(transaction.activationDate || transaction.date)}
              </div>
            </div>
            <ChevronDown className={`h-5 w-5 text-silver transition-transform ${
              expandedMobileTx === transaction.id ? 'rotate-180' : ''
            }`} />
          </div>
        </div>
        
        {expandedMobileTx === transaction.id && (
          <div className="p-4 border-t border-border">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-secondary/50 p-3 rounded-lg">
                <div className="text-xs text-silver font-body mb-1">Buyer</div>
                <div className="text-sm font-bold text-foreground font-heading">
                  {transaction.userName}
                </div>
                <div className="text-xs text-muted-foreground font-body">
                  ID: {transaction.memberId}
                </div>
              </div>
              <div className="bg-secondary/50 p-3 rounded-lg">
                <div className="text-xs text-silver font-body mb-1">Package</div>
                <div className="text-sm font-bold text-foreground font-heading">
                  {transaction.packageName}
                </div>
                <div className="text-xs text-muted-foreground font-body">
                  {transaction.packageId}
                </div>
              </div>
            </div>

            {/* Amount Breakdown */}
            <div className="mb-4">
              <h4 className="text-muted-foreground font-semibold text-xs flex items-center mb-2 font-heading">
                <DollarSign className="h-3 w-3 mr-2" />
                Investment Details
              </h4>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-secondary/50 p-2 rounded-lg">
                  <div className="text-xs text-silver font-body mb-1">Amount</div>
                  <div className="text-sm font-bold text-foreground font-heading">
                    ${transaction.baseAmount?.toFixed(2)}
                  </div>
                </div>
                {transaction.dailyROI && (
                  <div className="bg-secondary/50 p-2 rounded-lg">
                    <div className="text-xs text-silver font-body mb-1">Daily ROI</div>
                    <div className="text-sm font-bold text-green-400 font-heading">
                      {transaction.dailyROI}%
                    </div>
                  </div>
                )}
                <div className="bg-secondary/50 p-2 rounded-lg border border-green-500/30">
                  <div className="text-xs text-silver font-body mb-1">Total</div>
                  <div className="text-lg font-bold text-green-400 font-heading">
                    ${transaction.amount?.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>

            {/* Receiver Info */}
            {isGift && transaction.targetUserName && (
              <div className="mb-4">
                <h4 className="text-muted-foreground font-semibold text-xs flex items-center mb-2 font-heading">
                  <Gift className="h-3 w-3 mr-2" />
                  Gifted To
                </h4>
                <div className="bg-secondary/50 border border-border rounded-lg p-3">
                  <div className="text-xs text-silver font-body mb-1">Receiver</div>
                  <div className="text-sm font-bold text-foreground font-heading">
                    {transaction.targetUserName}
                  </div>
                  <div className="text-xs text-muted-foreground font-body">
                    ID: {transaction.targetUserId}
                  </div>
                </div>
              </div>
            )}

            {/* Additional Info */}
            <div className="space-y-2 pt-4 border-t border-border">
              <div className="flex justify-between text-xs">
                <span className="text-silver font-body">Transaction ID:</span>
                <span className="text-foreground font-mono">{transaction.transactionId}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-silver font-body">Activation Date:</span>
                <span className="text-foreground">{formatDate(transaction.activationDate || transaction.date)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-silver font-body">Payment Method:</span>
                <span className="text-yellow-400">{transaction.paymentMethod || 'Wallet'}</span>
              </div>
              {transaction.durationDays && (
                <div className="flex justify-between text-xs">
                  <span className="text-silver font-body">Duration:</span>
                  <span className="text-foreground">{transaction.durationDays} days</span>
                </div>
              )}
              {transaction.directUplineId && (
                <div className="flex justify-between text-xs">
                  <span className="text-silver font-body">Upline ID:</span>
                  <span className="text-purple-400 font-mono">{transaction.directUplineId}</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="pt-4 border-t border-border">
              <button
                onClick={() => handleViewTransaction(transaction)}
                className="w-full px-4 py-2 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg transition-colors font-body flex items-center justify-center text-sm"
              >
                <Eye className="h-4 w-4 mr-2" />
                View Full Details
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="max-w-full mx-auto px-4">
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading activation reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto px-4">
      {showTransactionDialog && <TransactionDialog />}
      
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-foreground font-heading">Package Activation Report</h1>
        <p className="text-muted-foreground mt-1 text-sm lg:text-base font-body">
          View all package activations • {stats.total} total packages • ${stats.totalAmount.toFixed(0)} total investment
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Total Activations */}
        <div className="card-glow p-4">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center mr-3 icon-electric">
              <Package className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="text-muted-foreground text-xs mb-1 font-body">Total Packages</div>
              <div className="text-xl font-bold text-foreground font-heading">{stats.total}</div>
              <div className="text-primary text-sm font-body">${stats.totalAmount.toFixed(0)} total</div>
            </div>
          </div>
        </div>

        {/* Active Packages */}
        <div className="card-glow p-4">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center mr-3 icon-electric">
              <CheckCircle className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <div className="text-muted-foreground text-xs mb-1 font-body">Active Packages</div>
              <div className="text-xl font-bold text-foreground font-heading">{stats.active}</div>
              <div className="text-green-400 text-sm font-body">${stats.activeAmount.toFixed(0)} active</div>
            </div>
          </div>
        </div>

        {/* Expired Packages */}
        <div className="card-glow p-4">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center mr-3 icon-electric">
              <XCircle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <div className="text-muted-foreground text-xs mb-1 font-body">Expired Packages</div>
              <div className="text-xl font-bold text-foreground font-heading">{stats.expired}</div>
              <div className="text-red-400 text-sm font-body">${stats.expiredAmount.toFixed(0)} expired</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-secondary/30 border border-border rounded-2xl p-4 lg:p-6 mb-6 card-glow">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Filter className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold text-foreground font-heading">Filter Activations</h2>
            <span className="text-xs text-muted-foreground bg-secondary px-2 py-1 rounded font-body">
              {filteredTransactions.length} found
            </span>
          </div>
          <button
            onClick={exportToCSV}
            disabled={filteredTransactions.length === 0}
            className={`px-4 py-2 rounded-lg transition-all flex items-center justify-center text-sm border ${
              filteredTransactions.length === 0
                ? 'bg-muted text-muted-foreground border-border cursor-not-allowed'
                : 'btn-electric text-primary-foreground font-semibold'
            }`}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </button>
        </div>

        {/* Desktop Filters */}
        <div className="hidden lg:grid grid-cols-5 gap-4">
          {/* Search */}
          <div>
            <label className="block text-muted-foreground mb-1 text-xs font-body">Search</label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Name, Member ID, Package..."
                className="w-full pl-7 pr-2 py-1.5 bg-secondary/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary text-xs font-body"
              />
            </div>
          </div>

          {/* Type Filter */}
          <div>
            <label className="block text-muted-foreground mb-1 text-xs font-body">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full bg-secondary/50 border border-border rounded-lg px-3 py-1.5 text-foreground focus:outline-none focus:border-primary text-xs font-body"
            >
              <option value="all">All Types</option>
              <option value="package_gift">Gifted Packages</option>
              <option value="package_purchase">Self Purchases</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-muted-foreground mb-1 text-xs font-body">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full bg-secondary/50 border border-border rounded-lg px-3 py-1.5 text-foreground focus:outline-none focus:border-primary text-xs font-body"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          {/* Date Filter */}
          <div>
            <label className="block text-muted-foreground mb-1 text-xs font-body">Date Range</label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full bg-secondary/50 border border-border rounded-lg px-3 py-1.5 text-foreground focus:outline-none focus:border-primary text-xs font-body"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
          </div>

          {/* Sort By */}
          <div>
            <label className="block text-muted-foreground mb-1 text-xs font-body">Sort By</label>
            <div className="flex gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="flex-1 bg-secondary/50 border border-border rounded-lg px-3 py-1.5 text-foreground focus:outline-none focus:border-primary text-xs font-body"
              >
                <option value="date">Activation Date</option>
                <option value="amount">Amount</option>
                <option value="name">Buyer Name</option>
                <option value="package">Package Name</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="w-10 bg-secondary/50 border border-border rounded-lg flex items-center justify-center text-foreground hover:bg-secondary/70 transition-colors"
                title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
              >
                <ArrowUpDown className="h-3 w-3" />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Filters */}
        <div className="lg:hidden space-y-3">
          <div>
            <label className="block text-muted-foreground mb-1 text-xs font-body">Search</label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search activations..."
                className="w-full pl-7 pr-2 py-1.5 bg-secondary/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary text-xs font-body"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-muted-foreground mb-1 text-xs font-body">Type</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full bg-secondary/50 border border-border rounded-lg px-3 py-1.5 text-foreground focus:outline-none focus:border-primary text-xs font-body"
              >
                <option value="all">All Types</option>
                <option value="package_gift">Gifted</option>
                <option value="package_purchase">Self Purchase</option>
              </select>
            </div>
            
            <div>
              <label className="block text-muted-foreground mb-1 text-xs font-body">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full bg-secondary/50 border border-border rounded-lg px-3 py-1.5 text-foreground focus:outline-none focus:border-primary text-xs font-body"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden lg:block card-glow overflow-hidden mb-8">
        <div className="overflow-x-auto">
          <table className="w-full min-w-max">
            <thead className="bg-secondary/50">
              <tr className="text-xs">
                <th className="py-2 px-1 text-left text-muted-foreground font-semibold font-heading w-32">Txn ID</th>
                <th className="py-2 px-1 text-center text-muted-foreground font-semibold font-heading w-20">Type</th>
                <th className="py-2 px-1 text-left text-muted-foreground font-semibold font-heading w-36">Buyer / Member ID</th>
                <th className="py-2 px-1 text-left text-muted-foreground font-semibold font-heading w-32">Package</th>
                <th className="py-2 px-1 text-right text-muted-foreground font-semibold font-heading w-20">Amount</th>
                <th className="py-2 px-1 text-left text-muted-foreground font-semibold font-heading w-32">Activation Date</th>
                <th className="py-2 px-1 text-center text-muted-foreground font-semibold font-heading w-20">Status</th>
                <th className="py-2 px-1 text-center text-muted-foreground font-semibold font-heading w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {filteredTransactions.length > 0 ? (
                filteredTransactions.map((transaction) => (
                  <DesktopTableRow key={transaction.id} transaction={transaction} />
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="py-16 text-center">
                    <div className="text-muted-foreground text-6xl mb-4">📦</div>
                    <h3 className="text-xl font-semibold text-foreground mb-2 font-heading">
                      {transactions.length === 0 ? 'No Package Activations!' : 'No Activations Found'}
                    </h3>
                    <p className="text-muted-foreground font-body">
                      {transactions.length === 0 
                        ? 'There are no package activations in the system.' 
                        : 'Try adjusting your search filters'}
                    </p>
                    {transactions.length > 0 && (
                      <button
                        onClick={() => {
                          setSearchTerm('');
                          setStatusFilter('all');
                          setTypeFilter('all');
                          setDateFilter('all');
                        }}
                        className="mt-4 px-6 py-2 btn-electric text-primary-foreground font-semibold rounded-xl transition-all"
                      >
                        Clear All Filters
                      </button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Accordion View */}
      <div className="lg:hidden">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-foreground font-heading">Package Activations ({filteredTransactions.length})</h2>
        </div>
        {filteredTransactions.length > 0 ? (
          filteredTransactions.map((transaction, index) => (
            <MobileAccordionItem key={transaction.id} transaction={transaction} index={index} />
          ))
        ) : (
          <div className="card-glow p-8 text-center">
            <div className="text-muted-foreground text-6xl mb-4">📦</div>
            <h3 className="text-xl font-semibold text-foreground mb-2 font-heading">
              {transactions.length === 0 ? 'No Package Activations!' : 'No Activations Found'}
            </h3>
            <p className="text-muted-foreground font-body">
              {transactions.length === 0 
                ? 'There are no package activations in the system.' 
                : 'Try adjusting your search filters'}
            </p>
            {transactions.length > 0 && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setTypeFilter('all');
                  setDateFilter('all');
                }}
                className="mt-4 px-6 py-2 btn-electric text-primary-foreground font-semibold rounded-xl transition-all"
              >
                Clear All Filters
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivationReport;
